import { PageId, WidgetId } from 'types/idTypes';
import { InfographState, PageSize, WidgetsMap } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { pipe } from 'utils/array';
import { GroupWidgetData } from 'widgets/GroupWidget/GroupWidget.types';
import {
  getFlattenedLayerOrder,
  getGroupParentMap,
  getWidgetTypeFromId,
  isWidgetWithinPage,
} from 'widgets/Widget.helpers';
import { getCombinedRect, getWidgetRect, isWidgetContained } from 'widgets/WidgetLayout.helpers';
import { WidgetRect } from 'widgets/WidgetLayout.types';

import { FULL_SIZE_OFFSET } from './ResponsiveLinkView.config';
import { findWidgetsOnTopOf } from './ResponsiveLinkView.helpers';
import { isValidHorizontalSplit, isValidVerticalSplit, kMeans } from './kMeans';
import { regionGrowing } from './regionGrowing';
import { DecorativeWidget, WidgetSegmentGroup, WidgetSegmentGroupMember } from './segmentAndGroupWidgets.types';

// Can be considered as a decorative full-sized widget
const DECORATIVE_WIDGET_TYPES: Partial<Record<WidgetType, boolean>> = {
  [WidgetType.BasicShape]: true,
  [WidgetType.Image]: true,
};

/**
 * Checks if a widget is a decorative widget.
 *
 * @param widgetId - The ID of the widget to check.
 * @returns boolean
 */
const isDecorativeWidget = (widgetId: WidgetId): boolean => {
  if (!widgetId) return false;

  const widgetType = getWidgetTypeFromId(widgetId);
  return !!DECORATIVE_WIDGET_TYPES[widgetType];
};

/**
 * Finds decorative widgets from a array of widgetIds
 * A decorative widget won't be considered as a part of a group
 *
 * @param widgetIdArr - An array of widget IDs.
 * @param widgets - WidgetsMap
 * @param pageSize - The size of the page.
 *
 * @returns An array of decorative widgets.
 */
const findDecorativeWidgets = (
  widgetIdArr: WidgetId[],
  widgets: WidgetsMap,
  pageSize: PageSize,
): DecorativeWidget[] => {
  const decorativeWidgets: DecorativeWidget[] = [];

  for (let i = 0; i < widgetIdArr.length; i++) {
    const widgetId = widgetIdArr[i];
    const widget = widgets[widgetId];

    if (isDecorativeWidget(widgetId)) {
      const isFullWidth = widget.widthPx + FULL_SIZE_OFFSET >= pageSize.widthPx;
      const isFullHeight = widget.heightPx + FULL_SIZE_OFFSET >= pageSize.heightPx;
      const isFullSize = isFullWidth && isFullHeight;

      if (isFullSize) {
        decorativeWidgets.push({ id: widgetId, type: 'full-size' });
        continue;
      }

      // doesn't contain full widgets
      if (isFullWidth || isFullHeight) {
        const membersOnTop = findWidgetsOnTopOf(widgetId, widgetIdArr, widgets);
        if (membersOnTop.length === 0) {
          decorativeWidgets.push({ id: widgetId, type: isFullWidth ? 'full-width' : 'full-height' });
        }
      }
    }
  }

  return decorativeWidgets;
};

// A layout widget must have one of these widgets on top to be considered a base widget
const BASE_LAYOUT_REQUIRED_WIDGET: Partial<Record<WidgetType, boolean>> = {
  [WidgetType.Table]: true,
  [WidgetType.Text]: true,
};

// can form a group if they have widgets on top of them
const BASE_LAYOUT_WIDGET_TYPES: Partial<Record<WidgetType, boolean>> = {
  [WidgetType.BasicShape]: true,
  [WidgetType.Image]: true,
  [WidgetType.Table]: true,
};

/**
 * Checks if a widget is a layout widget.
 * @param widgetId - The ID of the widget.
 * @returns Returns true if the widget is a layout widget, otherwise returns false.
 */
const isLayoutWidget = (widgetId: WidgetId): boolean => {
  if (!widgetId) return false;

  const widgetType = getWidgetTypeFromId(widgetId);
  return !!BASE_LAYOUT_WIDGET_TYPES[widgetType];
};

const BASE_LAYOUT = {
  containedThresholdPercentage: 40,
  fullContainedThresholdPercentage: 85,
  minWidgetWidth: 100,
  minWidgetHeight: 60,
  minWidgetSingleWidth: 50,
};
/**
 * Takes an array of widgetIds - and segments it into base layout groups
 * A base layout group is a layout widget with at least 1 [BASE_LAYOUT_REQUIRED_WIDGET] on top of it
 *
 * @param widgetIdArr - Array of widgetIds
 * @param widgets - The map of widgets.
 * @param groupParentMap - The map of group parent IDs.
 *
 * @returns Array of WidgetSegmentGroups
 */
const findBaseLayoutSegmentGroups = (
  widgetIdArr: WidgetId[],
  widgets: WidgetsMap,
  groupParentMap: { [id: WidgetId]: string },
  pageSize: PageSize,
): WidgetSegmentGroup[] => {
  // find the layout widgets
  const layoutWidgetIds: WidgetId[] = [];
  widgetIdArr.forEach((widgetId) => {
    if (isLayoutWidget(widgetId)) layoutWidgetIds.push(widgetId);
  });

  // find all widgets that are on top of the layout widget (e.g. shape/image)
  const baseWidgets: WidgetSegmentGroup[] = [];

  // A member can only be part of one group
  const memberMap = new Set();

  for (let i = 0; i < layoutWidgetIds.length; i++) {
    const layoutWidgetId = layoutWidgetIds[i];
    const baseWidgetData = widgets[layoutWidgetId];
    const baseRect = getWidgetRect(baseWidgetData);
    const layerIdx = widgetIdArr.indexOf(layoutWidgetId);
    const isFullWidth = baseRect.widthPx + FULL_SIZE_OFFSET >= pageSize.widthPx;

    if (baseRect.heightPx < BASE_LAYOUT.minWidgetHeight && !isFullWidth) continue;
    if (baseRect.widthPx < BASE_LAYOUT.minWidgetWidth && baseRect.heightPx < BASE_LAYOUT.minWidgetWidth) continue;
    if (baseRect.widthPx < BASE_LAYOUT.minWidgetSingleWidth) continue;

    const members = [];
    const ignoreNestedContainedRects: WidgetRect[] = [];

    // only widgets on top of the layoutwidget are considered
    for (let j = layerIdx + 1; j < widgetIdArr.length; j++) {
      const widgetId = widgetIdArr[j];
      const widget = widgets[widgetId];

      if (widgetId === layoutWidgetId) continue;
      if (memberMap.has(widgetId)) continue;

      const widgetRect = getWidgetRect(widget);

      if (!isLayoutWidget(widgetId) && widgetRect.heightPx > baseRect.heightPx) {
        continue;
      }

      const isContained = isWidgetContained(baseRect, widgetRect, BASE_LAYOUT.containedThresholdPercentage);

      // if the widget is a layout widget - it must be fully contained to be considered
      // e.g. a shape with a partially covered shape on top will not be considered
      if (isLayoutWidget(widgetId)) {
        const isFullyContained = isWidgetContained(baseRect, widgetRect, BASE_LAYOUT.fullContainedThresholdPercentage);
        if (!isFullyContained) {
          // anything on top of this widget should be ignored - if it has a text on top of it
          const widgetsOnTop = findWidgetsOnTopOf(widgetId, widgetIdArr, widgets);
          if (widgetsOnTop.length && widgetsOnTop.some((w) => getWidgetTypeFromId(w.id) === WidgetType.Text)) {
            ignoreNestedContainedRects.push(widgetRect);
          }
          continue;
        }
      }

      // check if the widget is on top of a widget that should be ignored
      if (ignoreNestedContainedRects.length) {
        if (
          ignoreNestedContainedRects.some((r: WidgetRect) =>
            isWidgetContained(r, widgetRect, BASE_LAYOUT.containedThresholdPercentage),
          )
        ) {
          continue;
        }
      }

      // if the widget is a layout widget - it must be fully contained to be considered
      // e.g. a shape with a partially covered shape on top will not be considered
      if (isLayoutWidget(widgetId)) {
        const isFullyContained = isWidgetContained(baseRect, widgetRect, BASE_LAYOUT.fullContainedThresholdPercentage);
        if (!isFullyContained) {
          // anything on top of this widget should be ignored - if it has a text on top of it
          const widgetsOnTop = findWidgetsOnTopOf(widgetId, widgetIdArr, widgets);
          if (widgetsOnTop.length && widgetsOnTop.some((w) => getWidgetTypeFromId(w.id) === WidgetType.Text)) {
            ignoreNestedContainedRects.push(widgetRect);
          }
          continue;
        }
      }

      // check if the widget is on top of a widget that should be ignored
      if (ignoreNestedContainedRects.length) {
        if (
          ignoreNestedContainedRects.some((r: WidgetRect) =>
            isWidgetContained(r, widgetRect, BASE_LAYOUT.fullContainedThresholdPercentage),
          )
        ) {
          continue;
        }
      }

      if (isContained) {
        members.push({ id: widgetId, rect: widgetRect });

        // if its a group member add all the members of the group
        const groupParentId = groupParentMap[widgetId];
        if (groupParentId) {
          const groupParent = widgets[groupParentId];
          const groupMembers = (groupParent as GroupWidgetData).memberWidgetIds || [];
          for (const memberId of groupMembers) {
            if (memberMap.has(memberId)) continue;
            members.push({ id: memberId, rect: getWidgetRect(widgets[memberId]) });
          }
        }
      }
    }

    if (!isConsideredBaseWidget(members, baseRect, pageSize, layoutWidgetId)) continue;

    // add the members to the memberMap so we don't add them to another group
    members.forEach((m) => memberMap.add(m.id));

    const combinedRect = getCombinedRect([baseRect, ...members.map((m) => m.rect)]);
    baseWidgets.push({ id: layoutWidgetId, members, ...combinedRect });
  }

  return baseWidgets;
};

/**
 * Determines whether a widget is considered a base widget based on its members and size
 *
 * @param members - The members of the widget segment group.
 * @param baseRect - The base rectangle of the widget.
 * @param pageSize - The size of the page.
 *
 * @returns true if the widget is considered a base widget, otherwise false.
 */
const isConsideredBaseWidget = (
  members: WidgetSegmentGroupMember[],
  baseRect: WidgetRect,
  pageSize: PageSize,
  widgetId: WidgetId,
) => {
  // if it is full size consider it as a base widget
  const isFullWidth = baseRect.widthPx + FULL_SIZE_OFFSET >= pageSize.widthPx;
  if (isFullWidth) {
    const hasImage = members.some((m) => getWidgetTypeFromId(m.id) === WidgetType.Image);
    if (hasImage) return true;
  }

  if (getWidgetTypeFromId(widgetId) === WidgetType.Table) return true;

  // there must be at least 1 of the BASE_LAYOUT_REQUIRED_WIDGET to be considered a base widget
  const isConsideredBaseWidget = members.some((m) => {
    const widgetType = getWidgetTypeFromId(m.id);
    if (BASE_LAYOUT_REQUIRED_WIDGET[widgetType]) return true;
    return false;
  });

  if (isConsideredBaseWidget) return true;
  return false;
};

/**
 * Finds the normal group widgets from a array of widgetIds
 * And returns them as an array with WidgetSegmentGroups
 *
 * @param widgetIdArr - The order of the widgets in the layer.
 * @param widgets - The map of widgets.
 * @returns An array of widget segment groups.
 */
const findNormalWidgetGroups = (widgetIdArr: WidgetId[], widgets: WidgetsMap): WidgetSegmentGroup[] => {
  const groupWidgets: WidgetSegmentGroup[] = [];

  for (let i = 0; i < widgetIdArr.length; i++) {
    const widgetId = widgetIdArr[i];
    const isGroup = getWidgetTypeFromId(widgetId) === WidgetType.Group;
    if (!isGroup) continue;

    const memberWidgetIds = (widgets[widgetId] as GroupWidgetData).memberWidgetIds;
    const members = [];
    for (const memberId of memberWidgetIds) {
      members.push({ id: memberId, rect: getWidgetRect(widgets[memberId]) });
    }

    const combinedRect = getCombinedRect([...members.map((m: any) => m.rect)]);
    groupWidgets.push({ id: widgetId, members, ...combinedRect });
  }

  return groupWidgets;
};

// Not included for now for region growing - might need to be handled separately later
const EXCLUDE_WIDGETS_FROM_ADJACENT: Partial<Record<WidgetType, boolean>> = {
  [WidgetType.Line]: false,
  [WidgetType.LineIcon]: false,
};

// proximity threshold to consider widgets as adjacent - e.g. within 40px of each other will be clustered together
const PROXIMITY_PX = 40;

/**
 * Takes an array of widgetIds - and segments it into groups based on proximity with the region growing algorithm
 *
 * @param widgetIdArr - Array of widgetIds
 * @param widgets - The map of widgets.
 * @param groupParentMap - The map of group parent IDs.
 * @returns An array of widget segment groups.
 */
const findAdjacentSegmentGroups = (
  widgetIdArr: WidgetId[],
  widgets: WidgetsMap,
  groupParentMap: { [WidgetId: WidgetId]: string },
): WidgetSegmentGroup[] => {
  // can be tuned for different widget types later if needed
  const groups = regionGrowing(widgetIdArr, PROXIMITY_PX, groupParentMap, widgets);
  const newGroups: WidgetSegmentGroup[] = [];

  // make sure we don't add the same widget to multiple groups
  const memberCache = new Set();

  groups.forEach((group) => {
    const members: WidgetSegmentGroupMember[] = [];
    group.forEach((w) => {
      if (memberCache.has(w.id)) {
      } else {
        memberCache.add(w.id);
        members.push({ id: w.id, rect: getWidgetRect(widgets[w.id]) });
      }
    });

    if (members.length) {
      const combinedRect = getCombinedRect(members.map((m) => m.rect));
      newGroups.push({ members, ...combinedRect });
    }
  });

  return newGroups;
};

const GRID = {
  minSplitHeight: 400,
  minHeightArea: 250,
  minWidth: 300,
  minWidthArea: 200,
  minVerticalSplitHeight: 50,
  // minimum number of "grid" groups
  minK: 2,
};
/**
 * Splits the widget segment groups into smaller "grid" groups based on the largest side of the group
 *
 * @param widgetSegmentGroup - The widget segment group to split.
 * @returns An array of widget segment groups.
 */
const splitIntoGrid = (widgetSegmentGroup: WidgetSegmentGroup[]) => {
  const newGroups: WidgetSegmentGroup[] = [];

  for (let i = 0; i < widgetSegmentGroup.length; i++) {
    const group = widgetSegmentGroup[i];

    // Only 1 member in the group cant be split
    if (group.members.length === 1) {
      newGroups.push(group);
      continue;
    }

    // split based on the largest side
    const largestSide = group.widthPx > group.heightPx ? 'width' : 'height';
    const groupMembersFiltered = group.members;

    // if the group has enough height - split horizontally into smaller groups
    // group
    //   -
    // group
    //   -
    // group
    let hasSplitHorizontally = false;
    if (largestSide === 'height' && group.heightPx >= GRID.minSplitHeight) {
      const maxK = Math.max(GRID.minK, Math.round(group.heightPx / GRID.minHeightArea));

      // iterate down from K to 2 until we find the best split
      for (let k = maxK; k >= GRID.minK; k--) {
        if (!hasSplitHorizontally) {
          const output = kMeans(groupMembersFiltered, k, 'horizontal');
          const splitGroups = output.groups.filter((g) => g.length >= 1);

          if (isValidHorizontalSplit(splitGroups)) {
            hasSplitHorizontally = true;
            splitGroups.forEach((g) => {
              const rect = getCombinedRect(g.map((m: WidgetSegmentGroupMember) => m.rect));
              newGroups.push({ members: g, ...rect });
            });
          }
        }
      }
    }

    // if the group has enough width - split vertically into smaller groups
    // group - group - group
    let hasSplitVertically = false;
    if (largestSide === 'width' && group.widthPx >= GRID.minWidth && group.heightPx >= GRID.minVerticalSplitHeight) {
      const maxK = Math.max(GRID.minK, Math.round(group.widthPx / GRID.minWidthArea));

      // iterate down from K to 2 until we find the best split
      for (let k = maxK; k >= GRID.minK; k--) {
        if (!hasSplitVertically) {
          const output = kMeans(groupMembersFiltered, k, 'vertical');
          const splitGroups = output.groups.filter((g) => g.length >= 1);

          if (isValidVerticalSplit(splitGroups)) {
            hasSplitVertically = true;
            splitGroups.forEach((g) => {
              const rect = getCombinedRect(g.map((m: WidgetSegmentGroupMember) => m.rect));
              newGroups.push({ members: g, ...rect });
            });
          }
        }
      }
    }

    if (!hasSplitVertically && !hasSplitHorizontally) newGroups.push(group);
  }

  return newGroups;
};

/**
 * Segments and groups widgets based on:
 * 1. rules of thumb (shape with x widgets on top is considered as a group)
 * 2. normal group widgets
 * 3. position proximity
 *
 * @param infographState - The state of the infograph.
 * @param pageId - The ID of the page.
 * @returns An object containing the segmented and grouped widgets.
 */
export const segmentAndGroupWidgets = (infographState: InfographState, pageId: PageId) => {
  const page = infographState.pages[pageId];
  const widgets = infographState.widgets;
  const layerOrder = page.widgetLayerOrder;
  const size = infographState.size;

  // remove widgets that are outside of the page
  const flattenedLayerOrder = getFlattenedLayerOrder(page, widgets).filter((id) =>
    isWidgetWithinPage(widgets, id, size),
  );

  // to lookup if a widget belongs to a group
  const groupParentMap = getGroupParentMap(layerOrder, widgets);

  // find the decorative widgets (e.g. full size images)
  const decorativeWidgets = findDecorativeWidgets(layerOrder, widgets, size);

  // remove decorative widgets from the layer order - not considered as part of groups
  const filteredFlattenedLayerOrder = flattenedLayerOrder.filter(
    (id) => !decorativeWidgets.find((shape) => shape.id === id),
  );

  // find the base layout widgets (e.g. shapes, images) with other widgets on top
  // e.g. a shape with text
  const baseLayoutWidgetGroups = findBaseLayoutSegmentGroups(
    filteredFlattenedLayerOrder,
    widgets,
    groupParentMap,
    size,
  );
  // find nearby/adjacent widgets and organize them into groups
  // exclude the widgets that have been grouped already
  const remainingWidgetsArr: WidgetId[] = filteredFlattenedLayerOrder
    .filter(
      (id) =>
        !baseLayoutWidgetGroups.find(
          (g: WidgetSegmentGroup) => g.id === id || g.members.find((m: WidgetSegmentGroupMember) => m.id === id),
        ),
    )
    .filter((id) => !EXCLUDE_WIDGETS_FROM_ADJACENT[getWidgetTypeFromId(id)]);

  const adjacentWidgetGroups = pipe(
    () => findAdjacentSegmentGroups(remainingWidgetsArr, widgets, groupParentMap),
    // split twice - first horizontally then vertically (or vice versa depending on the largest side)
    splitIntoGrid,
    splitIntoGrid,
  )([]);

  // find the "normal" manually grouped widgets (e.g. group widget)
  const normalGroups = findNormalWidgetGroups(layerOrder, widgets);

  // TODO: return these in a structured way so that we can scale/stack the groups responsively in the view
  return { normalGroups, baseLayoutWidgetGroups, adjacentWidgetGroups, decorativeWidgets };
};
